import React, { useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";

const GlowingParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    let mouse = { x: width / 2, y: height / 2, moved: false };

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.moved = true;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    const particles = [];
    // Increase particle count based on screen size to keep density high but performant
    const numParticles = Math.min(Math.floor((width * height) / 6000), 300);

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.size = Math.random() * 2 + 0.5;
        this.baseX = this.x;
        this.baseY = this.y;
        this.density = (Math.random() * 30) + 1;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      draw() {
        ctx.fillStyle = `rgba(220, 174, 82, ${this.opacity})`;
        ctx.shadowBlur = 15;
        ctx.shadowColor = "rgba(220, 174, 82, 0.8)";
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
        ctx.shadowBlur = 0; // reset
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Wrap around
        if (this.x < 0) this.x = width;
        if (this.x > width) this.x = 0;
        if (this.y < 0) this.y = height;
        if (this.y > height) this.y = 0;

        // Mouse attraction/repulsion
        if (mouse.moved) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          let forceDirectionX = dx / distance;
          let forceDirectionY = dy / distance;
          let maxDistance = 150;
          let force = (maxDistance - distance) / maxDistance;
          let directionX = forceDirectionX * force * this.density * 0.05;
          let directionY = forceDirectionY * force * this.density * 0.05;

          if (distance < maxDistance) {
            this.x += directionX;
            this.y += directionY;
            // increase opacity when near mouse
            this.opacity = Math.min(this.opacity + 0.02, 1);
          } else {
            // slowly fade back to normal
            if (this.opacity > 0.3) this.opacity -= 0.01;
            // slowly drift back to natural movement
          }
        }
      }
    }

    for (let i = 0; i < numParticles; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 2, /* Placed closely behind content but ahead of bg */
      }}
    />
  );
};

const QRButton = ({ to, src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <Link to={to} className="qr-fallback-btn">
        {alt}
      </Link>
    );
  }

  return (
    <Link to={to} className="qr-img-btn">
      <img
        src={src}
        alt={alt}
        onError={() => setHasError(true)}
      />
    </Link>
  );
};

export default function ScanRedirect() {
  return (
    <div className="qr-redirect-page">
      <GlowingParticles />
      <Container className="qr-content d-flex flex-column align-items-center text-center">
        {/* Logo */}
        <div className="qr-logo-container">
          <Link to="/">
             <img
               src="/logo.png"
               alt="BBR Logo"
               className="qr-logo"
             />
          </Link>
        </div>

        {/* Headings */}
        <div className="qr-headings">
          <div className="qr-welcome-line">
            <span className="qr-welcome-dash"></span>
            <span className="qr-welcome-txt">WELCOME TO BBR</span>
            <span className="qr-welcome-dash"></span>
          </div>
          
          <div className="qr-separator-glow">
             <div className="qr-separator-flare"></div>
          </div>
          
          <h1 className="qr-main-title mt-2">What Brings You Here</h1>
          <div className="qr-today-row">
             <div className="qr-today-line-left"></div>
             <div className="qr-today-wrapper">
                <span className="qr-main-title-gold">Today?</span>
                <img src="/assets/frontend/images/under_line.png" alt="underline" className="qr-today-underline-img" />
             </div>
             <div className="qr-today-line-right"></div>
          </div>
        </div>

        {/* Buttons (Images provided by client) */}
        <div className="qr-buttons-container">
          <QRButton 
             to="/" 
             src="/assets/frontend/images/btn-rent-boat.webp" 
             alt="Rent A Boat" 
          />
          <QRButton 
             to="/list-my-boat" 
             src="/assets/frontend/images/btn-list-boat.webp" 
             alt="List My Boat" 
          />
          <QRButton 
             to="/list-my-charter" 
             src="/assets/frontend/images/btn-list-charter.webp" 
             alt="List My Charter" 
          />
        </div>
      </Container>
      
      <style>{`
        /* Full Page Setup with new background image */
        .qr-redirect-page {
          min-height: 100vh;
          min-height: 100dvh;
          background-color: #000000;
          position: relative;
          overflow-x: hidden;
          overflow-y: auto;
          font-family: 'Montserrat', sans-serif;
          z-index: 1;
        }

        .qr-redirect-page::before {
          content: "";
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image: url("/assets/frontend/images/scan-bg.webp");
          background-position: center bottom;
          background-size: cover;
          background-repeat: no-repeat;
          filter: blur(2px);
          transform: scale(1.02); /* Prevents the blur from exposing dark screen edges */
          z-index: 0;
        }

        .qr-content {
          position: relative;
          z-index: 10;
          min-height: 100vh;
          min-height: 100dvh;
          padding: 3vh 15px;
          justify-content: center;
          gap: clamp(15px, 3vh, 50px);
        }

        .qr-logo {
          width: 250px;
          max-width: 45vw;
          max-height: 10vh;
          object-fit: contain;
          filter: drop-shadow(0 0 10px rgba(255,255,255,0.2));
        }

        /* Welcome Line */
        .qr-welcome-line {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 2px;
        }
        .qr-welcome-dash {
          height: 1px;
          width: 50px;
          background: linear-gradient(90deg, transparent, #dcae52);
        }
        .qr-welcome-line .qr-welcome-dash:last-child {
          background: linear-gradient(270deg, transparent, #dcae52);
        }
        .qr-welcome-txt {
          color: #dcae52;
          font-weight: 700;
          letter-spacing: 4px;
          margin: 0 15px;
          font-size: clamp(0.9rem, 2.5vh, 1.3rem);
          text-transform: uppercase;
        }

        /* Separator Glow below Welcome */
        .qr-separator-glow {
           width: 90%;
           max-width: 500px;
           height: 1.5px;
           margin: 1.5vh auto 2vh auto;
           background: linear-gradient(90deg, transparent 0%, rgba(220,174,82,0.2) 20%, rgba(220,174,82,1) 50%, rgba(220,174,82,0.2) 80%, transparent 100%);
           position: relative;
        }
        .qr-separator-flare {
           position: absolute;
           top: 50%; left: 50%;
           transform: translate(-50%, -50%);
           width: 60px; height: 1.5px;
           background: rgba(255, 255, 255, 0.8);
           box-shadow: 0 0 5px 1px rgba(220, 174, 82, 0.5);
           border-radius: 50%;
           opacity: 0.3;
        }

        /* Main Titles */
        .qr-headings {
          margin-bottom: clamp(10px, 3vh, 35px); /* Responsive spacer to prevent swoosh overlap */
        }

        .qr-main-title {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-size: clamp(1.6rem, 5.5vw, 3.8rem);
          color: #ffffff;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: clamp(10px, 2vh, 20px);
          letter-spacing: -1px;
        }

        .qr-today-row {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 90%;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .qr-today-line-left, .qr-today-line-right {
          flex-grow: 1;
          height: 1.5px;
          opacity: 0.8;
        }
        .qr-today-line-left {
          background: linear-gradient(90deg, transparent 0%, #dcae52 100%);
          margin-right: 35px;
        }
        .qr-today-line-right {
          background: linear-gradient(270deg, transparent 0%, #dcae52 100%);
          margin-left: 35px;
        }

        .qr-today-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .qr-main-title-gold {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-size: clamp(2.2rem, 7vw, 4.2rem);
          font-weight: 900;
          line-height: 1;
          letter-spacing: -0.5px;
          background: linear-gradient(180deg, #fef4ce 0%, #dcae52 35%, #fce9a3 45%, #9b6c16 55%, #e1b24e 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 6px 15px rgba(0,0,0,0.9)) drop-shadow(0 0 15px rgba(220, 174, 82, 0.3));
          display: inline-block;
          position: relative;
          z-index: 2;
        }
        
        /* Image Underline for Today? */
        .qr-today-underline-img {
          position: absolute;
          bottom: -45px; /* Pushed down further */
          left: 50%;
          transform: translateX(-50%);
          width: 145%; /* Wider to extend past T and ? */
          pointer-events: none;
          user-select: none;
          z-index: 1;
        }

        /* Image-based Buttons Styling */
        .qr-buttons-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: min(2vh, 20px);
          width: 100%;
        }

        .qr-img-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: min(85vw, 450px);
          max-height: 16vh;
          text-decoration: none;
          transition: transform 0.2s ease, filter 0.2s ease;
          position: relative;
          z-index: 5;
        }

        .qr-img-btn img {
          width: 100%;
          height: 100%;
          max-height: 16vh;
          object-fit: contain;
          display: block;
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
        }

        .qr-img-btn:hover {
          transform: scale(1.03);
          filter: drop-shadow(0 10px 20px rgba(0,0,0,0.8)) drop-shadow(0 0 15px rgba(221,180,103,0.3));
        }

        /* Fallback Button Styles */
        .qr-fallback-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 85vw;
          max-width: 450px;
          height: 12vh;
          min-height: 80px;
          background: linear-gradient(145deg, #1a1a1a 0%, #000000 100%);
          border: 2px solid #dcae52;
          border-radius: 50px;
          color: #ffffff;
          font-weight: 800;
          font-size: clamp(1.2rem, 4.5vw, 1.8rem);
          text-transform: uppercase;
          text-decoration: none;
          box-shadow: 0 5px 15px rgba(0,0,0,0.8), inset 0 0 10px rgba(220,174,82,0.2);
          transition: all 0.2s ease;
          letter-spacing: 1px;
        }

        .qr-fallback-btn:hover {
          transform: scale(1.03);
          color: #fef4ce;
          border-color: #fce9a3;
          box-shadow: 0 10px 20px rgba(0,0,0,0.8), inset 0 0 15px rgba(220,174,82,0.4);
        }

        @media (min-width: 992px) {
          .qr-content {
             justify-content: flex-start;
             padding-top: 4vh;
          }

          .qr-buttons-container {
            flex-direction: row;
            gap: 30px;
            margin-top: 3vh;
          }
          
          .qr-img-btn, .qr-fallback-btn {
             width: 30% !important;
             max-width: 400px !important;
             max-height: 20vh;
          }
          
          .qr-img-btn img {
             max-height: 20vh;
          }
        }
      `}</style>
    </div>
  );
}
